import React from "react";
import { Instagram, Play, ExternalLink, ShieldCheck } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

/**
 * InstagramCommunity
 * ──────────────────
 * A "social proof" section built around Gil's REAL Instagram screenshots
 * (profile header + feed grid). Visitors immediately see there is an active
 * collector community behind the project. No Instagram embed. No Instagram
 * API. No live widgets. The screenshots are decorative image assets that
 * make the section trustworthy and encourage visitors to click through to
 * the real profile.
 *
 * The screenshot URLs are owner-provided assets. If either URL is missing,
 * the section still renders gracefully.
 */

const PROFILE_SCREENSHOT_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/u99jus9n_C1355364-0B03-4375-AD68-B32682C8C896%20%281%29.jpeg";
const FEED_SCREENSHOT_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_27cb127a-2886-41a6-886c-e1d3db01f0ff/artifacts/hl8ze87j_820B8237-59F4-4C5E-8FF3-F69F0ABA0FC2.webp";

const HANDLE = "gilmaakaroun";
const DEFAULT_URL = `https://instagram.com/${HANDLE}`;


/**
 * Graceful <img> wrapper that swaps to a branded CollectorUp / Instagram
 * placeholder tile when the source fails to load. Prevents the broken-
 * image icon from ever appearing to a real customer.
 */
function InstagramScreenshotImage({ src, alt, className, loading, testid, fallbackLabel, minHeight = 260 }) {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return (
      <div
        className={`${className || ""} flex flex-col items-center justify-center text-center bg-gradient-to-br from-pink-50 via-white to-yellow-50 text-black/70 p-6`}
        style={{ minHeight }}
        data-testid={testid ? `${testid}-fallback` : "instagram-image-fallback"}
        role="img"
        aria-label={alt}
      >
        <Instagram size={38} className="text-pink-500 mb-3" />
        <div className="font-bold text-sm text-black">@{HANDLE}</div>
        <div className="text-[11px] uppercase tracking-widest text-black/60 mt-1">{fallbackLabel || "See Gil on Instagram"}</div>
        <div className="text-[10px] text-black/40 mt-3">Screenshot loading…</div>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      data-testid={testid}
      onError={() => setFailed(true)}
    />
  );
}

export default function InstagramCommunity() {
  const settings = useSiteSettings();
  const url = (settings.instagram_url || "").trim() || DEFAULT_URL;

  return (
    <section
      className="py-20 md:py-28 bg-[var(--ink)] text-white overflow-hidden"
      data-testid="home-instagram-community"
    >
      <div className="container-gm">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-16 items-center">

          {/* ── LEFT: copy + CTAs ────────────────────────────────────── */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest">
              <Instagram size={14} className="text-[var(--panini-yellow)]" /> Instagram Community
            </div>
            <h2 className="h-display text-white text-4xl md:text-6xl mt-6 leading-[1.02]">
              Real Collectors.<br />
              Real Events.<br />
              <span className="text-[var(--panini-yellow)]">Real Moments.</span>
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-lg text-base">
              See trading events, collector stories, album completions, Gold
              Crumple openings, certificate presentations, and everything
              happening in our Panini community.
            </p>

            {/* Live proof-point strip pulled from the profile screenshot */}
            <div
              className="mt-8 grid grid-cols-3 gap-3 md:gap-4 max-w-md"
              data-testid="instagram-proof-stats"
            >
              {[
                { v: "1,268", l: "Posts" },
                { v: "4,256", l: "Followers" },
                { v: "907",   l: "Following" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-2xl border border-white/15 bg-white/[0.04] px-3 py-3 text-center"
                  data-testid={`instagram-stat-${s.l.toLowerCase()}`}
                >
                  <div className="font-mono text-xl md:text-2xl text-white">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-yellow inline-flex items-center justify-center gap-2 min-h-[52px] px-6 py-3 text-sm md:text-base"
                data-testid="instagram-follow-btn"
              >
                <Instagram size={18} /> Follow @GilMaakaroun
              </a>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] px-6 py-3 text-sm md:text-base rounded-full border-2 border-white/40 text-white hover:bg-white/10"
                data-testid="instagram-reels-btn"
              >
                <Play size={18} /> View Reels &amp; Highlights
              </a>
            </div>

            <p className="mt-6 text-white/40 text-[10px] uppercase tracking-widest">
              Instagram screenshots shown as-is · Follow the real profile for live posts
            </p>
          </div>

          {/* ── RIGHT: two real screenshot cards ─────────────────────── */}
          <div className="relative" data-testid="instagram-screenshots">

            {/* Feed grid — larger, back layer */}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open Gil Maakaroun on Instagram"
              className="group block relative rounded-3xl border-2 border-white/15 bg-white shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)] overflow-hidden lg:ml-16"
              data-testid="instagram-feed-card"
            >
              <div className="px-5 py-3 border-b border-black/10 bg-white flex items-center gap-2 text-black">
                <Instagram size={16} className="text-black/70" />
                <span className="font-bold text-sm">@{HANDLE}</span>
                <span className="text-black/40 text-xs">· Feed</span>
                <ExternalLink size={13} className="ml-auto text-black/40 group-hover:text-black transition-colors" />
              </div>
              <InstagramScreenshotImage
                src={FEED_SCREENSHOT_URL}
                alt="Gil Maakaroun Instagram feed — trading events, sticker swaps, album completions and Panini community moments"
                className="block w-full h-auto object-contain bg-white"
                loading="lazy"
                testid="instagram-feed-image"
                fallbackLabel="Latest events, swaps and album completions"
              />
            </a>

            {/* Profile header — front card, overlapping bottom-left */}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open @gilmaakaroun profile"
              className="group hidden md:block absolute -bottom-8 -left-2 lg:left-0 w-[62%] max-w-[380px] rounded-2xl border-2 border-white/25 bg-white shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] overflow-hidden"
              data-testid="instagram-profile-card"
            >
              <div className="px-4 py-2 border-b border-black/10 bg-white flex items-center gap-2 text-black">
                <Instagram size={14} className="text-black/70" />
                <span className="font-bold text-xs">@{HANDLE}</span>
                <ShieldCheck size={12} className="text-blue-500" />
                <span className="text-black/40 text-[10px] ml-auto">Profile</span>
              </div>
              <InstagramScreenshotImage
                src={PROFILE_SCREENSHOT_URL}
                alt="Gil Maakaroun verified Instagram profile — 1,268 posts, 4,256 followers, Digital creator"
                className="block w-full h-auto object-contain bg-white"
                loading="lazy"
                testid="instagram-profile-image"
                fallbackLabel="@gilmaakaroun · verified profile"
                minHeight={160}
              />
            </a>

            {/* Mobile: stack the profile card underneath instead of overlapping */}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open @gilmaakaroun profile"
              className="md:hidden mt-5 block rounded-2xl border-2 border-white/25 bg-white shadow-[0_18px_40px_-15px_rgba(0,0,0,0.7)] overflow-hidden"
              data-testid="instagram-profile-card-mobile"
            >
              <div className="px-4 py-2 border-b border-black/10 bg-white flex items-center gap-2 text-black">
                <Instagram size={14} className="text-black/70" />
                <span className="font-bold text-xs">@{HANDLE}</span>
                <ShieldCheck size={12} className="text-blue-500" />
                <span className="text-black/40 text-[10px] ml-auto">Profile</span>
              </div>
              <InstagramScreenshotImage
                src={PROFILE_SCREENSHOT_URL}
                alt="Gil Maakaroun verified Instagram profile — 1,268 posts, 4,256 followers, Digital creator"
                className="block w-full h-auto object-contain bg-white"
                loading="lazy"
                fallbackLabel="@gilmaakaroun · verified profile"
                minHeight={160}
              />
            </a>

            {/* Decorative "sticker" badges */}
            <div className="hidden lg:block absolute -top-5 -right-3 rotate-[8deg] pointer-events-none">
              <span className="sticker sticker-yellow">REAL PROFILE</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
